import { HttpClient } from './core/httpClient';
import { SessionService } from './sessionService';
import { ConversationService } from './conversationService';
import { MessageService } from './messageService';

/**
 * Servicio principal de TIA GPC (Facade Pattern)
 * Orquesta los servicios especializados y provee una interfaz unificada
 *
 * Arquitectura:
 * - HttpClient: Manejo de peticiones HTTP y errores
 * - SessionService: Gestión de sesiones
 * - ConversationService: Gestión de conversaciones
 * - MessageService: Envío de mensajes y transcripción
 */
export class GPCService {
  constructor(baseURL, licenseToken) {
    if (!licenseToken) {
      throw new Error('License token is required');
    }

    this.licenseToken = licenseToken;

    this.httpClient = new HttpClient(baseURL, licenseToken);

    this.sessionService = new SessionService(licenseToken, this.httpClient);
    this.conversationService = new ConversationService(
      licenseToken,
      this.sessionService.getSessionToken(),
      this.httpClient
    );
    this.messageService = new MessageService(
      licenseToken,
      this.sessionService.getSessionToken(),
      this.httpClient
    );
  }


  getSessionToken() {
    return this.sessionService.getSessionToken();
  }

  hasValidCachedSession() {
    return this.sessionService.hasValidCachedSession();
  }

  async validateSession() {
    return await this.sessionService.validateSession();
  }

  validateSessionLocally() {
    this.sessionService.validateSessionLocally();
  }

  renewSessionToken() {
    const newToken = this.sessionService.renewSessionToken();
    this.conversationService.sessionToken = newToken;
    this.messageService.sessionToken = newToken;
    return newToken;
  }

  async releaseSession() {
    return await this.sessionService.releaseSession();
  }


  async listConversations(limit = 20) {
    return await this.conversationService.listConversations(limit);
  }

  async getConversationHistory(conversationId, limit = 50) {
    return await this.conversationService.getConversationHistory(conversationId, limit);
  }

  async getCurrentConversation() {
    return await this.conversationService.getCurrentConversation();
  }

  saveLastConversation(conversationId, messages) {
    return this.conversationService.saveLastConversation(conversationId, messages);
  }

  getLastConversation(maxAge = 24 * 60 * 60 * 1000) {
    return this.conversationService.getLastConversation(maxAge);
  }

  clearLastConversation() {
    return this.conversationService.clearLastConversation();
  }

  async updateConversationTitle(conversationId, newTitle) {
    return await this.conversationService.updateConversationTitle(conversationId, newTitle);
  }

  async deleteConversation(conversationId) {
    return await this.conversationService.deleteConversation(conversationId);
  }

  async sendMessage(message, onChunk, conversationId = null) {
    if (!this.licenseToken) {
      throw new Error('License token is required.');
    }

    return await this.messageService.sendMessage(
      message,
      onChunk,
      conversationId
    );
  }

  async transcribeAudio(audioBlob) {
    if (!this.licenseToken) {
      throw new Error('License token is required.');
    }

    return await this.messageService.transcribeAudio(audioBlob);
  }

  async getInitConfig() {
    if (!this.licenseToken) {
      throw new Error('License token is required.');
    }

    return await this.httpClient.get('/init');
  }

  async checkHealth() {
    try {
      const response = await this.httpClient.get('/health');
      return response;
    } catch (error) {
      return {
        status: 'unhealthy',
        error: error.message
      };
    }
  }

  async generateCaptchaChallenge() {
    if (!this.licenseToken) {
      throw new Error('License token is required.');
    }

    return await this.httpClient.post('/captcha/challenge', {
      session_token: this.getSessionToken()
    });
  }

  async verifyCaptchaAnswer(challengeId, answer) {
    if (!this.licenseToken) {
      throw new Error('License token is required.');
    }

    return await this.httpClient.post('/captcha/verify', {
      challenge_id: challengeId,
      answer: parseInt(answer, 10),
      session_token: this.getSessionToken()
    });
  }

  async verifyMcpCredentials(loginData) {
    if (!this.licenseToken) {
      throw new Error('License token is required.');
    }

    return await this.httpClient.post('/mcp/verify-credentials', {
      login_data: loginData,
      session_token: this.getSessionToken()
    });
  }
}


let instance = null;


export const getGPCService = (baseURL, licenseToken) => {
  if (!instance || (licenseToken && instance.licenseToken !== licenseToken)) {
    instance = new GPCService(baseURL, licenseToken);
  }
  return instance;
};


export const resetGPCService = () => {
  if (instance) {
    instance.releaseSession().catch(() => {
      console.warn('[GPCService] Error releasing session during reset');
    });
  }
  instance = null;
};